import type { IdentityLookupRow, JourneyTierStatus } from '../api/identityVerificationDashboardApi';

export type IdentityAttemptRole = 'existing_found' | 'after_signin' | 'identity_check';

export type IdentityAttemptPresentation = {
  row: IdentityLookupRow;
  role: IdentityAttemptRole;
  /** Chip on the attempt card, e.g. "Existing customer found". */
  roleLabel: string;
  /** Short supporting line under the role chip. */
  roleHint: string;
  isLatest: boolean;
};

const LINK_PENDING_STATUSES = new Set([
  'PENDING_EXISTING_ACCOUNT_LINK',
  'LINKED_PENDING_AUTH',
]);

const COMPLETED_STATUSES = new Set([
  'SUCCESS',
  'COMPLETED',
  'IDV_COMPLETED',
]);

function statusOf(row: IdentityLookupRow): string {
  return String(row.session_status ?? '').toUpperCase();
}

function routeOf(row: IdentityLookupRow): string {
  return String(row.verification_route ?? '').toUpperCase();
}

export function classifyIdentityAttemptRole(
  row: IdentityLookupRow,
  opts?: { applicationHadExistingFound?: boolean },
): IdentityAttemptRole {
  const status = statusOf(row);
  const route = routeOf(row);
  const bio = row.biometricMatchComparison?.kind;

  if (
    LINK_PENDING_STATUSES.has(status) ||
    status === 'REJECTED_DUPLICATE' ||
    row.latestDedup?.isDuplicate === true
  ) {
    return 'existing_found';
  }

  if (route === 'RETURNING_SELFIE_1TO1' || bio === 'RETURNING_SELFIE_1TO1') {
    return 'after_signin';
  }

  // Post-OTP continuity: completed after an earlier existing-customer hit on this application.
  if (
    opts?.applicationHadExistingFound &&
    COMPLETED_STATUSES.has(status) &&
    row.ccid &&
    row.latestDedup?.isDuplicate === false
  ) {
    return 'after_signin';
  }

  return 'identity_check';
}

export function roleLabelFor(role: IdentityAttemptRole): string {
  switch (role) {
    case 'existing_found':
      return 'Existing customer found';
    case 'after_signin':
      return 'After sign-in';
    default:
      return 'Identity check';
  }
}

export function roleHintFor(role: IdentityAttemptRole): string {
  switch (role) {
    case 'existing_found':
      return 'This submission matched someone already on file. Sign-in was required before continuing.';
    case 'after_signin':
      return 'Customer signed in (OTP), then identity was checked again against the same account.';
    default:
      return 'Full identity check for this attempt.';
  }
}

/**
 * Append-only operator history for the application.
 *
 * Never collapse attempts: a customer may re-upload a different document and later
 * pass, but admins must still be able to inspect what failed on every earlier attempt.
 */
export function presentIdentityAttempts(rows: IdentityLookupRow[]): IdentityAttemptPresentation[] {
  if (rows.length === 0) return [];

  const chronological = [...rows].sort(
    (a, b) => new Date(a.created_at).getTime() - new Date(b.created_at).getTime(),
  );

  const hadExistingFound = chronological.some(
    (row) => classifyIdentityAttemptRole(row) === 'existing_found',
  );

  const classified = chronological.map((row) => ({
    row,
    role: classifyIdentityAttemptRole(row, { applicationHadExistingFound: hadExistingFound }),
  }));

  // Newest first for the tab.
  const newestFirst = [...classified].sort(
    (a, b) => new Date(b.row.created_at).getTime() - new Date(a.row.created_at).getTime(),
  );

  return newestFirst.map((item, index) => ({
    row: item.row,
    role: item.role,
    roleLabel: roleLabelFor(item.role),
    roleHint: roleHintFor(item.role),
    isLatest: index === 0,
  }));
}

const DETAIL_SIGNAL_LABELS: Array<{ key: string; whenTrue: string }> = [
  { key: 'exact', whenTrue: 'Exact document number' },
  { key: 'match', whenTrue: 'Document number' },
  { key: 'full_name_match', whenTrue: 'Full name' },
  { key: 'ordered_name_match', whenTrue: 'Name order' },
  { key: 'token_set_name_match', whenTrue: 'Name tokens' },
  { key: 'dob_match', whenTrue: 'Date of birth' },
  { key: 'gender_match', whenTrue: 'Gender' },
];

/**
 * Plain “what matched” lines for admins — no raw tier metric dumps.
 */
export function summarizeDedupWhatMatched(
  tiers: JourneyTierStatus[],
  dedupMessage: string | null | undefined,
): string[] {
  const fromMessage = parseMatchedLabelsFromMessage(dedupMessage);
  if (fromMessage.length > 0) return fromMessage;

  const lines: string[] = [];
  for (const tier of tiers) {
    if (!tier.hit) continue;
    const label = (tier.label || tier.tier || '').trim();
    if (!label) continue;
    const signals = signalsFromTierDetails(tier.details);
    if (signals.length > 0) {
      lines.push(`${label}: ${signals.join(', ')}`);
    } else {
      lines.push(label);
    }
  }
  return lines;
}

function parseMatchedLabelsFromMessage(message: string | null | undefined): string[] {
  if (!message) return [];
  const m = message.match(/Duplicate detected on:\s*(.+)$/i);
  if (!m?.[1]) return [];
  return m[1]
    .split(',')
    .map((s) => s.trim())
    .filter(Boolean);
}

function signalsFromTierDetails(details: Record<string, unknown> | null | undefined): string[] {
  if (!details) return [];
  const out: string[] = [];
  for (const { key, whenTrue } of DETAIL_SIGNAL_LABELS) {
    if (details[key] === true) out.push(whenTrue);
  }
  if (typeof details.biometric_distance === 'number') {
    out.push(`Selfie distance ${details.biometric_distance}`);
  }
  // Deduplicate while preserving order
  return [...new Set(out)];
}

/** Brief Mitek / document vendor line for application Identity (max ~2 sentences). */
export function summarizeDocumentVendorBrief(row: IdentityLookupRow): {
  headline: string;
  detail: string | null;
} | null {
  if (!row.mitekCalled) return null;
  const auth = row.dossier?.findings?.authenticated;
  const primary = row.evidenceProcessingPrimary?.primaryMessage?.trim() || null;
  if (auth === true) {
    return {
      headline: 'Mitek verification passed.',
      detail: primary || 'Document authenticated for the claimed identity.',
    };
  }
  if (auth === false) {
    return {
      headline: 'Mitek verification failed.',
      detail: primary || 'Document could not be authenticated.',
    };
  }
  if (primary) {
    return { headline: 'Mitek verification needs review.', detail: primary };
  }
  return {
    headline: 'Mitek verification ran.',
    detail: 'Authenticity outcome was not a clear pass or fail.',
  };
}
